import { stripImageFileExtension } from '@/utils/imageUrlUtils';

/**
 * 从 SKU货号提取编号配对键：最右边一个「-」左侧；无「-」则用整段。
 * 例：ABC-DEF-001 → ABC-DEF
 */
export function extractSkuPairingKey(skuCode) {
  const raw = String(skuCode || '').trim();
  if (!raw) return '';
  // 去掉常见占位后缀，避免干扰匹配
  const cleaned = raw.replace(/印花图名称/g, '').trim();
  if (!cleaned) return '';
  const lastDash = cleaned.lastIndexOf('-');
  if (lastDash <= 0) return cleaned;
  return cleaned.slice(0, lastDash).trim();
}

function fileNameFromUrl(url) {
  const raw = String(url || '').trim();
  if (!raw) return '';
  try {
    const path = raw.split('?')[0].split('#')[0];
    const seg = path.substring(path.lastIndexOf('/') + 1);
    return stripImageFileExtension(decodeURIComponent(seg || '') || '');
  } catch {
    return '';
  }
}

export function normalizeImagePairingName(nameOrUrl) {
  const fromName = stripImageFileExtension(String(nameOrUrl || '').trim());
  if (fromName) return fromName.toLowerCase();
  const fromUrl = fileNameFromUrl(nameOrUrl);
  return fromUrl ? fromUrl.toLowerCase() : '';
}

/**
 * 从图片名提取编号配对键：去扩展名后，取最右边一个「-」左侧（与 SKU 提取规则一致）。
 * 例：ABC-DEF-001.jpg → ABC-DEF；ABC-DEF-.png → ABC-DEF
 */
export function extractImagePairingKey(nameOrUrl) {
  const basename = stripImageFileExtension(String(nameOrUrl || '').trim())
    || fileNameFromUrl(nameOrUrl);
  return extractSkuPairingKey(basename);
}

function resolveImagePairingKey(item) {
  if (!item) return '';
  return extractImagePairingKey(item.name)
    || extractImagePairingKey(item.url)
    || extractImagePairingKey(item.previewUrl);
}

/**
 * 在 detailImages 中按「图片名提取键」与 skuKey 精确匹配（忽略大小写）。
 * @returns {{ url: string, name?: string } | null}
 */
export function matchDetailImageBySkuKey(detailImages, skuCode) {
  // 与 matchNth 一致：对 SKU / 图片名都做「最后 - 左侧」提取后再比
  const key = extractSkuPairingKey(skuCode).toLowerCase();
  if (!key) return null;
  const list = Array.isArray(detailImages) ? detailImages : [];
  for (const item of list) {
    if (!item) continue;
    const nameKey = resolveImagePairingKey(item).toLowerCase();
    if (nameKey && nameKey === key) {
      const url = String(item.url || item.previewUrl || '').trim();
      if (url) return { ...item, url, name: item.name || nameKey };
    }
  }
  return null;
}

/** 从「第N张」解析 0-based 下标，默认 0；兼容「第 1 张」空格与纯数字 */
export function parsePairingImageIndex(rightValue) {
  const raw = String(rightValue || '').trim();
  const match = raw.match(/第\s*(\d+)\s*张/) || raw.match(/^(\d+)$/);
  if (!match) return 0;
  const n = parseInt(match[1], 10);
  if (!Number.isFinite(n) || n < 1) return 0;
  return n - 1;
}

function toPairingImageResult(item, nameKey) {
  const url = String(item?.url || item?.previewUrl || '').trim();
  if (!url) return null;
  return { ...item, url, name: item.name || nameKey || '' };
}

/**
 * 编号配对诊断：供导出控制台打印成功/失败原因。
 * @returns {{
 *   ok: boolean,
 *   reason: string,
 *   skuCode: string,
 *   skuKey: string,
 *   rightValue: string,
 *   index: number,
 *   detailCount: number,
 *   absoluteName?: string,
 *   absoluteKey?: string,
 *   matchedCount: number,
 *   image?: { url: string, name?: string } | null,
 *   path?: 'absolute' | 'matched-list' | null,
 * }}
 */
export function diagnoseNthDetailImagePairing(detailImages, skuCode, rightValue) {
  const skuRaw = String(skuCode || '').trim();
  const skuKey = extractSkuPairingKey(skuRaw).toLowerCase();
  const list = Array.isArray(detailImages) ? detailImages : [];
  const index = parsePairingImageIndex(rightValue);
  const base = {
    ok: false,
    reason: '',
    skuCode: skuRaw,
    skuKey,
    rightValue: String(rightValue || '第1张'),
    index,
    detailCount: list.length,
    matchedCount: 0,
    image: null,
    path: null,
  };

  if (!skuKey) {
    return { ...base, reason: 'sku_empty_or_no_key' };
  }
  if (list.length === 0) {
    return { ...base, reason: 'detail_images_empty' };
  }

  const absolute = list[index];
  const absoluteKey = absolute ? resolveImagePairingKey(absolute).toLowerCase() : '';
  const absoluteName = absolute
    ? String(absolute.name || absolute.url || absolute.previewUrl || '').trim()
    : '';

  if (absolute) {
    if (absoluteKey && absoluteKey === skuKey) {
      const image = toPairingImageResult(absolute, absoluteKey);
      if (image) {
        return {
          ...base,
          ok: true,
          reason: 'absolute_hit',
          absoluteName,
          absoluteKey,
          matchedCount: 1,
          image,
          path: 'absolute',
        };
      }
      return {
        ...base,
        reason: 'absolute_key_ok_but_url_empty',
        absoluteName,
        absoluteKey,
      };
    }
  } else {
    // 继续走同键列表回退
  }

  const matched = [];
  for (const item of list) {
    if (!item) continue;
    const nameKey = resolveImagePairingKey(item).toLowerCase();
    if (nameKey && nameKey === skuKey) matched.push(item);
  }

  const picked = matched[index] || null;
  if (picked) {
    const image = toPairingImageResult(picked, skuKey);
    if (image) {
      return {
        ...base,
        ok: true,
        reason: 'matched_list_hit',
        absoluteName,
        absoluteKey,
        matchedCount: matched.length,
        image,
        path: 'matched-list',
      };
    }
  }

  // 两段货号（如 SKC=1001-B）被抽成 1001，而图名 1001-B-1 的键是 1001-B。
  // 此时用完整货号按 SKC 规则再配一次，避免导出空图。
  const fullKey = extractSkcPairingKey(skuRaw).toLowerCase();
  if (fullKey && fullKey !== skuKey) {
    const matchedFull = [];
    for (const item of list) {
      if (!item) continue;
      const nameKey = resolveImagePairingKey(item).toLowerCase();
      if (nameKey && nameKey === fullKey) matchedFull.push(item);
    }
    const pickedFull = matchedFull[index] || null;
    if (pickedFull) {
      const image = toPairingImageResult(pickedFull, fullKey);
      if (image) {
        return {
          ...base,
          ok: true,
          reason: 'full_sku_as_skc_hit',
          skuKey: fullKey,
          absoluteName,
          absoluteKey,
          matchedCount: matchedFull.length,
          image,
          path: 'full-sku-as-skc',
        };
      }
    }
  }

  let reason = 'no_match';
  if (!absolute) reason = 'absolute_index_out_of_range';
  else if (!absoluteKey) reason = 'absolute_image_no_pairing_key';
  else if (absoluteKey !== skuKey) reason = 'absolute_key_mismatch';
  if (matched.length === 0) reason = `${reason}+no_sku_key_images`;
  else if (!picked) reason = `${reason}+matched_list_index_miss`;

  return {
    ...base,
    reason,
    absoluteName,
    absoluteKey,
    matchedCount: matched.length,
  };
}

/**
 * 取组内第 N 张图，用其「名称提取键」与 SKU 提取键比对；一致则返回该图。
 * 绝对下标未命中时：回退为「与 SKU 键匹配的图片列表」中的第 N 张（仍 1-based）。
 * @returns {{ url: string, name?: string } | null}
 */
export function matchNthDetailImageBySkuKey(detailImages, skuCode, rightValue) {
  return diagnoseNthDetailImagePairing(detailImages, skuCode, rightValue).image || null;
}

/**
 * SKC 配对键：使用完整 SKC货号（不去掉末段「-xxx」），以便与图名去掉序号后的键对齐。
 * 例：SKC=ABC-RED，图名 ABC-RED-1.jpg → 图键 ABC-RED → 命中。
 */
export function extractSkcPairingKey(skcCode) {
  const raw = String(skcCode || '').trim();
  if (!raw) return '';
  return raw.replace(/印花图名称/g, '').trim();
}

/**
 * SKC 动态值（与结果图相同的「第 N 张」语义）：
 * - 导出图本身已是一组；「第 N 张」= 组内第 N 个位置。
 * - 若图名能按 SKC货号筛出子组，则在子组内按位置取；否则对整组按位置取。
 * - 不做编号配对那种「绝对下标必须键一致」的校验。
 */
export function diagnoseNthDetailImagePairingBySkc(detailImages, skcCode, rightValue) {
  const skcRaw = String(skcCode || '').trim();
  const skcKey = extractSkcPairingKey(skcRaw).toLowerCase();
  const list = Array.isArray(detailImages) ? detailImages : [];
  const index = parsePairingImageIndex(rightValue);
  const base = {
    ok: false,
    reason: '',
    skcCode: skcRaw,
    skcKey,
    rightValue: String(rightValue || '第1张'),
    index,
    detailCount: list.length,
    matchedCount: 0,
    image: null,
    path: null,
  };

  if (list.length === 0) {
    return { ...base, reason: 'detail_images_empty' };
  }

  let group = list;
  let path = 'group-position';
  if (skcKey) {
    const matched = [];
    for (const item of list) {
      if (!item) continue;
      const nameKey = resolveImagePairingKey(item).toLowerCase();
      if (nameKey && nameKey === skcKey) matched.push(item);
    }
    if (matched.length > 0) {
      group = matched;
      path = 'skc-group';
    }
  }

  const picked = group[index] || null;
  if (!picked) {
    return {
      ...base,
      reason: 'group_index_miss',
      matchedCount: group.length,
      path,
    };
  }

  const image = toPairingImageResult(picked, skcKey || resolveImagePairingKey(picked));
  if (!image) {
    return {
      ...base,
      reason: 'group_url_empty',
      matchedCount: group.length,
      path,
    };
  }

  return {
    ...base,
    ok: true,
    reason: 'group_position_hit',
    matchedCount: group.length,
    image,
    path,
  };
}

export function matchNthDetailImageBySkcKey(detailImages, skcCode, rightValue) {
  return diagnoseNthDetailImagePairingBySkc(detailImages, skcCode, rightValue).image || null;
}
